// src/game/obstacles.ts
// Hay obstacle lifecycle (spec §4, Phase 4). A "hay" block has a `cover` count (1–2): while covered
// its color is hidden under a hay texture, and each slide reveals one layer until it becomes a normal
// colored block. This is the SAFE obstacle: cover NEVER affects movement or exits (the engine ignores
// it — see block.ts translateBlock, which preserves cover), so dropping hay onto an already-solvable
// generated level cannot change solvability. The recorded solution still clears the board; the cover
// is purely a visual reveal step layered on top. Therefore this lives entirely outside rules.ts and
// the generator — no re-verification needed (§4 "ship-ready, safe").

import type { GameState } from './rules';
import type { BlockId } from './types';
import type { ResolvedDifficulty } from '../gen/difficulty';
import { createBlock, decrementCover, isCovered } from './block';

/** In the open-ended 'variety' band, a covered block occasionally gets a second layer. */
const DOUBLE_COVER_CHANCE = 0.35;

/**
 * Return a copy of `state` with hay cover added to a random subset of blocks, per the band's
 * `allowHay` / `hayChance`. Deterministic for a given `rng`. A no-op (returns the SAME reference)
 * when the band forbids hay or no block is covered, so non-hay levels are untouched.
 */
export function applyHay(
  state: GameState,
  difficulty: ResolvedDifficulty,
  rng: () => number,
): GameState {
  if (!difficulty.allowHay || difficulty.hayChance <= 0) {
    return state;
  }
  let changed = false;
  const blocks = state.blocks.map((b) => {
    if (rng() < difficulty.hayChance) {
      const cover = difficulty.band === 'variety' && rng() < DOUBLE_COVER_CHANCE ? 2 : 1;
      changed = true;
      return createBlock({ ...b, cover });
    }
    return b;
  });
  return changed ? { board: state.board, blocks } : state;
}

/**
 * Reveal step: after block `blockId` slides and STOPS on the board, peel one hay layer. cover 2→1,
 * cover 1→normal, uncovered→unchanged. Returns the SAME reference when nothing changes. Call this on
 * the engine's post-slide state (NOT on an exit — an exited block is already gone).
 */
export function revealOnSlide(state: GameState, blockId: BlockId): GameState {
  let changed = false;
  const blocks = state.blocks.map((b) => {
    if (b.id === blockId && isCovered(b)) {
      changed = true;
      return decrementCover(b);
    }
    return b;
  });
  return changed ? { board: state.board, blocks } : state;
}
